import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconLayer, ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { useTheme } from '@mui/material';
import { Layer, PickingInfo } from '@deck.gl/core';
import { Capacitor } from '@capacitor/core';
import { throttle } from 'es-toolkit';
import { useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { useMapBearing } from 'hooks/useMapBearing';
import {
  DeckLayerGroups,
  useDeckLayers,
} from 'features/mapping/components/DeckOverlay';
import { AircraftLayerFeature, AircraftLayerFeatureCollection } from './types';
import {
  AircraftRotorInfo,
  aircraftBodyIconKey,
  aircraftBodyShadowIconKey,
  aircraftIconKey,
  aircraftRotorIconKey,
  aircraftShadowIconKey,
} from './aircraftIconAtlas';
import { useAircraftIconAtlas } from './useAircraftIconAtlas';
import { AnimatedRotorLayer, RotorClock } from './AnimatedRotorLayer';
import {
  castsShadow,
  getHitRadiusPx,
  getIconName,
  getRotorPhaseOffsetMs,
  isAircraftLabelVisible,
  getIconScaleMultiplier,
  getIconVariant,
  getPosition,
  getRotorHubPixelOffset,
  getRotorRevolutionMs,
  getRotorSpinDeg,
  getSize,
  isGroundedVariant,
  sortFeaturesForDrawing,
} from './AircraftDeckLayers.utils';
import {
  LABEL_BACKGROUND_PADDING_PX,
  LABEL_BACKGROUND_RADIUS_PX,
  LABEL_FONT_SIZE_PX,
  SHADOW_COLOR_RGBA,
  cssColorToDeckColor,
  getAircraftLabelPixelOffset,
  getAircraftShadowPixelOffset,
  getIconScaleForZoom,
} from './AircraftsLayer.styles';

const SHADOW_LAYER_ID = 'aircraft-shadows';
const BODY_LAYER_ID = 'aircraft-bodies';
const ROTOR_LAYER_ID = 'aircraft-rotors';
const CLICK_TARGET_LAYER_ID = 'aircraft-click-targets';
const LABEL_LAYER_ID = 'aircraft-labels';

// Transparent for the click target layer.
const CLICK_TARGET_LAYER_FILL_COLOR: [number, number, number, number] = [
  0, 0, 0, 0,
];

const NO_LAYERS: Layer[] = [];

const ZOOM_THROTTLE_MS = 100;

type AircraftDeckLayersProps = {
  data: AircraftLayerFeatureCollection;
  onClick: (feature: AircraftLayerFeature) => void;
  onHover: (feature: AircraftLayerFeature | null) => void;
  visible: boolean;
};

/** One drawn rotor: an aircraft paired with one of its icon's rotors. */
type RotorInstance = {
  feature: AircraftLayerFeature;
  rotorIndex: number;
  rotor: AircraftRotorInfo;
  durationMs: number;
  /** How far into the revolution this aircraft's rotors start. */
  phaseMs: number;
};

/**
 * Renders the aircraft icons and their labels into the map's shared deck.gl
 * overlay.
 */
export const AircraftDeckLayers = (props: AircraftDeckLayersProps): null => {
  const { data, onClick, onHover, visible } = props;
  const map = useMapInstance();
  const theme = useTheme();
  const mapBearing = useMapBearing() ?? 0;
  const atlas = useAircraftIconAtlas(visible);
  const isMobileApp = Capacitor.isNativePlatform();

  const [zoom, setZoom] = useState<number>(0);

  // Written by the rotor layer on each frame it draws, read by `getAngle`.
  // useRef to avoid React redraws when it changes.
  const rotorClockRef = useRef<RotorClock>({ nowMs: 0 });

  const iconScale = getIconScaleForZoom(zoom);

  // See https://dmitripavlutin.com/react-throttle-debounce/ for why
  // useMemo is used instead of useCallback.
  const updateZoom = useMemo(
    () =>
      throttle(() => {
        setZoom(map?.getZoom() ?? 0);
      }, ZOOM_THROTTLE_MS),
    [map],
  );

  useEffect(() => {
    return () => {
      updateZoom.cancel();
    };
  }, [updateZoom]);

  // Set the zoom correctly on initial load and when the layer becomes visible.
  // `visible` is in the deps because zooming while hidden doesn't fire the
  // `zoom` handler, so it would otherwise stay stale until the next zoom.
  useEffect(() => {
    updateZoom();
  }, [updateZoom, visible]);

  useMapEvent('zoom', updateZoom, map, visible);

  const features = useMemo(
    () => sortFeaturesForDrawing(data.features),
    [data.features],
  );

  // Rebuilt only when the data (aircraft) change.
  const rotorInstances = useMemo((): RotorInstance[] => {
    if (!atlas) {
      return [];
    }
    return features.flatMap((feature) => {
      const entry = atlas.rotors[getIconName(feature)];
      if (!entry) {
        return [];
      }
      // The phase (time constant added to its angle) is deterministically calculated from the aircraft's hex code.
      // This ensures it doesn't reset when new aircraft are loaded, and so that multi-rotor aircraft (Chinook)
      // rotors are in sync.
      const phaseMs = getRotorPhaseOffsetMs(
        feature.properties.hex,
        entry.animationDurationMs,
      );
      return entry.rotors.map((rotor, rotorIndex) => ({
        feature,
        rotorIndex,
        rotor,
        durationMs: entry.animationDurationMs,
        phaseMs,
      }));
    });
  }, [atlas, features]);

  const shadowFeatures = useMemo(
    () => features.filter(castsShadow),
    [features],
  );

  // The map's cursor before an aircraft was hovered; null when none is hovered.
  const cursorBeforeHoverRef = useRef<string | null>(null);

  const restoreCursor = useCallback((): void => {
    const canvas = map?.getCanvas();
    if (canvas && cursorBeforeHoverRef.current !== null) {
      canvas.style.cursor = cursorBeforeHoverRef.current;
    }
    cursorBeforeHoverRef.current = null;
  }, [map]);

  const handleHover = useCallback(
    (info: PickingInfo<AircraftLayerFeature>): void => {
      const feature = info.object ?? null;
      onHover(feature);

      if (!feature) {
        restoreCursor();
        return;
      }

      // Deck's own cursor styles its canvas, which doesn't receive pointer
      // events in overlay mode, so set it on the map's canvas instead.
      const canvas = map?.getCanvas();
      if (canvas && cursorBeforeHoverRef.current === null) {
        cursorBeforeHoverRef.current = canvas.style.cursor;
        canvas.style.cursor = 'pointer';
      }
    },
    [map, onHover, restoreCursor],
  );

  // Hiding the layer removes the hit target without deck sending a hover-off.
  useEffect(() => {
    if (!visible) {
      restoreCursor();
      onHover(null);
    }
  }, [visible, restoreCursor, onHover]);

  const handleClick = useCallback(
    (info: PickingInfo<AircraftLayerFeature>): boolean => {
      if (!info.object) {
        return false;
      }
      onClick(info.object);
      // Stop the click reaching layers underneath.
      return true;
    },
    [onClick],
  );

  const layers = useMemo(() => {
    if (!atlas || !visible || !features.length) {
      return NO_LAYERS;
    }

    // Mapbox rotates clockwise from North. Deck.gl's rotates counter-clockwise. This aligns the two.
    const aircraftAngle = (feature: AircraftLayerFeature): number =>
      mapBearing - feature.properties.rotate;

    const shared = {
      iconAtlas: atlas.atlasUrl,
      iconMapping: atlas.mapping,
      sizeUnits: 'pixels' as const,
      sizeScale: iconScale,
    };

    const aircraftShared = {
      ...shared,
      data: features,
      getPosition,
      getSize,
      getAngle: aircraftAngle,
    };

    return [
      new IconLayer<AircraftLayerFeature>({
        ...aircraftShared,
        id: SHADOW_LAYER_ID,
        data: shadowFeatures,
        getIcon: (feature) => {
          const name = getIconName(feature);
          const variant = getIconVariant(feature);
          return atlas.rotors[name]
            ? // Helicopter shadows are just the body, excluding rotors.
              aircraftBodyShadowIconKey(name, variant)
            : aircraftShadowIconKey(name, variant);
        },
        getColor: SHADOW_COLOR_RGBA,
        getPixelOffset: (feature) =>
          getAircraftShadowPixelOffset({
            altitude: feature.properties.altitude,
            scale: iconScale,
          }),
        pickable: false,
        updateTriggers: {
          getAngle: mapBearing,
          getPixelOffset: iconScale,
        },
      }),
      new IconLayer<AircraftLayerFeature>({
        ...aircraftShared,
        id: BODY_LAYER_ID,
        getIcon: (feature) => {
          const name = getIconName(feature);
          const variant = getIconVariant(feature);
          return atlas.rotors[name]
            ? // Helicopters draw their body here, and rotors below.
              aircraftBodyIconKey(name, variant)
            : aircraftIconKey(name, variant);
        },
        // The hit layer below is the click target.
        pickable: false,
        updateTriggers: {
          getAngle: mapBearing,
        },
      }),
      new AnimatedRotorLayer<RotorInstance>({
        ...shared,
        id: ROTOR_LAYER_ID,
        data: rotorInstances,
        clock: rotorClockRef.current,
        getPosition: (instance) => getPosition(instance.feature),
        getSize: (instance) => getSize(instance.feature),
        getIcon: (instance) =>
          aircraftRotorIconKey(
            getIconName(instance.feature),
            getIconVariant(instance.feature),
            instance.rotorIndex,
          ),
        getAngle: (instance) => {
          const heading = aircraftAngle(instance.feature);
          return (
            heading +
            getRotorSpinDeg({
              elapsedMs: rotorClockRef.current.nowMs + instance.phaseMs,
              durationMs: getRotorRevolutionMs(
                instance.durationMs,
                isGroundedVariant(getIconVariant(instance.feature)),
              ),
              direction: instance.rotor.direction,
            })
          );
        },
        getPixelOffset: (instance) =>
          getRotorHubPixelOffset({
            hubX: instance.rotor.hubX,
            hubY: instance.rotor.hubY,
            angleDeg: aircraftAngle(instance.feature),
            scale: iconScale * getIconScaleMultiplier(instance.feature),
          }),
        pickable: false,
        updateTriggers: {
          getAngle: [mapBearing],
          getPixelOffset: [mapBearing, iconScale],
        },
      }),
      // hover/click target for the aircraft. last in the list means it's drawn on top.
      new ScatterplotLayer<AircraftLayerFeature>({
        id: CLICK_TARGET_LAYER_ID,
        data: features,
        getPosition,
        radiusUnits: 'pixels',
        getRadius: (feature) => getHitRadiusPx(feature, iconScale),
        getFillColor: CLICK_TARGET_LAYER_FILL_COLOR,
        pickable: true,
        onClick: handleClick,
        onHover: isMobileApp ? undefined : handleHover,
        updateTriggers: {
          getRadius: iconScale,
        },
      }),
    ];
  }, [
    atlas,
    visible,
    features,
    shadowFeatures,
    rotorInstances,
    iconScale,
    mapBearing,
    handleClick,
    handleHover,
    isMobileApp,
  ]);

  const labelFeatures = useMemo(
    () => features.filter((feature) => isAircraftLabelVisible(feature, zoom)),
    [features, zoom],
  );

  /**
   * Registered as its own group so it draws after every icon, rather than being
   * interleaved with the aircraft it belongs to. Kept out of the `layers` memo
   * above so the rotor tick doesn't rebuild it.
   */
  const labelLayers = useMemo(() => {
    if (!visible || !labelFeatures.length) {
      return NO_LAYERS;
    }

    return [
      new TextLayer<AircraftLayerFeature>({
        id: LABEL_LAYER_ID,
        data: labelFeatures,
        getPosition,
        getText: (feature) => feature.properties.label,
        getSize: LABEL_FONT_SIZE_PX,
        sizeUnits: 'pixels',
        getColor: cssColorToDeckColor(theme.palette.text.primary),
        // Sits the chip above the aircraft, the way the symbol layer's `bottom`
        // text anchor and negative offset did.
        getAlignmentBaseline: 'bottom',
        getPixelOffset: getAircraftLabelPixelOffset(iconScale),
        fontFamily: theme.typography.fontFamily ?? 'sans-serif',
        background: true,
        getBackgroundColor: cssColorToDeckColor(theme.palette.background.paper),
        backgroundPadding: LABEL_BACKGROUND_PADDING_PX,
        backgroundBorderRadius: LABEL_BACKGROUND_RADIUS_PX,
        // The icons' hit targets own picking; a chip shouldn't swallow a click.
        pickable: false,
        updateTriggers: {
          getPixelOffset: iconScale,
        },
      }),
    ];
  }, [visible, labelFeatures, iconScale, theme]);

  useDeckLayers(DeckLayerGroups.Aircraft, layers);
  useDeckLayers(DeckLayerGroups.AircraftLabels, labelLayers);

  return null;
};
